import { z } from 'zod';
import { addMonths } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { MAD_CODE_PATTERN } from './dnv-mad.js';
import { SURVEY_PRINCIPLES, type SurveyPrinciple } from './equipment.js';
import { integer, localDate, option, optionalLines, text, uuid } from './fields.js';

/**
 * Klas: BV kalem listesi, başmühendis sörveyi ve sörveyör teyidi (plan M7 Task 22, 25; ADR 0029
 * K31) [NR467 Pt A Ch 2 App 1, App 2]. Saf modül. Sonuç raporlama anında gemide hesaplanır ve
 * değiştirilemez iş kaydına kopyalanır; bulut paket modunda yeniden hesaplamaz (K35).
 */

/**
 * Kalem kategorisi (K31). `tailshaft` pervane şaftıdır; PMS'te ara şaft başmühendise açıktır
 * [App 2 2.1.2], pervane şaftı hiçbir zaman [App 2 2.1.3]. İçe aktarmada "Shaft" pervane şaftı
 * okunur (daha sıkı okuma; ADR 0029 Ek: M7d).
 */
export const CLASS_ITEM_CATEGORIES = [
  'machinery',
  'electrical',
  'pressure_vessel',
  'boiler',
  'tailshaft',
  'other',
] as const;
export type ClassItemCategory = (typeof CLASS_ITEM_CATEGORIES)[number];

/**
 * Başmühendisin sörvey edemediği kalemler: PMS'te basınçlı kaplar [NR467 Pt A Ch 2 App 2 2.1.2];
 * pervane şaftı ve kazan hiçbir zaman [App 2 2.1.3].
 */
export const CE_FORBIDDEN_CATEGORIES = ['pressure_vessel', 'tailshaft', 'boiler'] as const;

/** İş kaydındaki `ce_rejection_code` değerleri; kayıt yine yazılır (K31). */
export const CE_REJECTION_CODES = [
  'UNKNOWN_BV_ITEM',
  'CE_SURVEY_NOT_ALLOWED',
  'CE_NOT_AUTHORIZED',
] as const;
export type CeRejectionCode = (typeof CE_REJECTION_CODES)[number];

/** Kalem kodu karşılaştırması: büyük-küçük harf ve baş-son boşluk fark etmez. */
export function classItemKey(code: string): string {
  return code.normalize('NFC').trim().toLocaleLowerCase('en');
}

export interface CeRecord {
  /** İşin "CE survey" işareti (iş tanımından). */
  ceSurvey: boolean;
  /** İşin yapıldığı gemi yerel günü. */
  doneOn: string;
  /** Kaydı raporlayan ve imzalayan kişi [App 2 4.1.2]. */
  reportedBy: string;
  /** Ekipmanın BV kalem kodu (`equipment.bv_item_code`). */
  bvItemCode: string | null;
}

export interface ClassItemRef {
  itemCode: string;
  category: string;
  surveyPrinciple: string | null;
}

export interface CeAuthorizationRef {
  id: string;
  userId: string;
  validFrom: string;
  validTo: string | null;
}

/** İş kaydına kopyalanan sonuç (kolon adlarıyla). */
export interface CeOutcome {
  ce_credit: boolean | null;
  ce_authorization_id: string | null;
  class_item_code: string | null;
  class_item_category: string | null;
  ce_rejection_code: CeRejectionCode | null;
}

/** Raporlayanın o gündeki geçerli yetki beyanı; birden çoksa en son başlayan. */
function validAuthorization(record: CeRecord, authorizations: readonly CeAuthorizationRef[]) {
  return [...authorizations]
    .filter(
      (a) =>
        a.userId === record.reportedBy &&
        a.validFrom <= record.doneOn &&
        (a.validTo === null || record.doneOn <= a.validTo),
    )
    .sort((a, b) =>
      a.validFrom > b.validFrom ? -1 : a.validFrom < b.validFrom ? 1 : a.id < b.id ? -1 : 1,
    )[0];
}

/**
 * Başmühendis sörveyinin sonucu (K31; saf):
 * - işaretsiz kayıtta kopya boştur;
 * - kalem listede yoksa sayılmaz (`UNKNOWN_BV_ITEM`) [App 1 2.4.1];
 * - basınçlı kap, pervane şaftı ve kazan ya da sörveyör prensibindeki kalem sayılmaz
 *   (`CE_SURVEY_NOT_ALLOWED`) [App 2 2.1.2–2.1.3];
 * - raporlayanın o gün geçerli yetki beyanı yoksa sayılmaz (`CE_NOT_AUTHORIZED`)
 *   [App 2 1.1.2, App 1 5.2.7].
 * Bakım kaydı her durumda yazılır; yalnız klas için sayılmaz.
 */
export function ceOutcome(
  record: CeRecord,
  item: ClassItemRef | null,
  authorizations: readonly CeAuthorizationRef[],
): CeOutcome {
  if (!record.ceSurvey) {
    return {
      ce_credit: null,
      ce_authorization_id: null,
      class_item_code: null,
      class_item_category: null,
      ce_rejection_code: null,
    };
  }
  const authorization = validAuthorization(record, authorizations);
  const base = {
    ce_authorization_id: authorization?.id ?? null,
    class_item_code: item?.itemCode ?? record.bvItemCode,
    class_item_category: item?.category ?? null,
  };
  let rejection: CeRejectionCode | null = null;
  if (item === null) rejection = 'UNKNOWN_BV_ITEM';
  else if (
    (CE_FORBIDDEN_CATEGORIES as readonly string[]).includes(item.category) ||
    item.surveyPrinciple === 'surveyor'
  ) {
    rejection = 'CE_SURVEY_NOT_ALLOWED';
  } else if (!authorization) rejection = 'CE_NOT_AUTHORIZED';
  return { ...base, ce_credit: rejection === null, ce_rejection_code: rejection };
}

/** Kalem kodu listede olmayan ekipman (uyarı; `UNKNOWN_BV_ITEM`). */
export function unknownClassItems(
  equipment: readonly { id: string; bvItemCode: string | null }[],
  items: readonly { itemCode: string }[],
): { equipmentId: string; bvItemCode: string; code: 'UNKNOWN_BV_ITEM' }[] {
  const known = new Set(items.map((i) => classItemKey(i.itemCode)));
  return equipment.flatMap((e) =>
    e.bvItemCode !== null && e.bvItemCode.trim() !== '' && !known.has(classItemKey(e.bvItemCode))
      ? [{ equipmentId: e.id, bvItemCode: e.bvItemCode, code: 'UNKNOWN_BV_ITEM' as const }]
      : [],
  );
}

/* ---------- BV kalem listesi [NR467 Pt A Ch 2 App 1 2.2.1c, 2.4.1] ---------- */

export const classItemInputSchema = z.strictObject({
  itemCode: text(32),
  description: text(200),
  category: option(CLASS_ITEM_CATEGORIES),
  surveyPrinciple: option(SURVEY_PRINCIPLES),
  intervalMonths: integer(1, 120)
    .nullish()
    .transform((value) => value ?? null),
});
export type ClassItemInput = z.output<typeof classItemInputSchema>;

export function parseClassItemInput(input: unknown): ParseResult<ClassItemInput> {
  const parsed = classItemInputSchema.safeParse(input);
  return parsed.success
    ? { ok: true, value: parsed.data }
    : { ok: false, issues: toIssues(parsed.error) };
}

/** İçe aktarma dosyasının sütunları (plan Task 25). */
export const CLASS_ITEM_COLUMNS = [
  'Item Code',
  'Description',
  'Category',
  'Survey Principle',
  'Interval (months)',
] as const;
type Column = (typeof CLASS_ITEM_COLUMNS)[number];

/**
 * İsteğe bağlı sütun: kalemin DNV klas bileşen kodu (`classcomponent`; ADR 0132 §4). Varsa BV kalem
 * kodu eşleşen ve henüz DNV kodu olmayan ekipmana yazılır; yoksa içe aktarma eskisi gibidir.
 */
export const CLASS_ITEM_DNV_COLUMN = 'DNV Code';
const DNV_CODE_MAX = 64;

const FIELD_OF: Record<Column, keyof ClassItemInput> = {
  'Item Code': 'itemCode',
  Description: 'description',
  Category: 'category',
  'Survey Principle': 'surveyPrinciple',
  'Interval (months)': 'intervalMonths',
};
const COLUMN_OF = Object.fromEntries(Object.entries(FIELD_OF).map(([c, f]) => [f, c])) as Record<
  keyof ClassItemInput,
  Column
>;

const label = (value: unknown) =>
  String(value ?? '')
    .normalize('NFC')
    .trim()
    .toLocaleLowerCase('en')
    .replace(/[\s\-/]+/g, '_');

const CATEGORY_ALIASES: Readonly<Record<string, ClassItemCategory>> = {
  machinery: 'machinery',
  electrical: 'electrical',
  pressure_vessel: 'pressure_vessel',
  pressure_vessels: 'pressure_vessel',
  boiler: 'boiler',
  boilers: 'boiler',
  tailshaft: 'tailshaft',
  tail_shaft: 'tailshaft',
  propeller_shaft: 'tailshaft',
  shaft: 'tailshaft',
  other: 'other',
};

const PRINCIPLE_ALIASES: Readonly<Record<string, SurveyPrinciple>> = {
  pms_ce: 'pms_ce',
  pms: 'pms_ce',
  ce: 'pms_ce',
  chief_engineer: 'pms_ce',
  surveyor: 'surveyor',
  condition_monitoring: 'condition_monitoring',
  cm: 'condition_monitoring',
  none: 'none',
};

export interface ClassItemRowError {
  /** Dosyadaki satır numarası (başlık 1). */
  row: number;
  column: Column | typeof CLASS_ITEM_DNV_COLUMN | null;
  code: string;
}

export interface ClassItemImport {
  /** `dnvCode`: isteğe bağlı DNV sütunu; sütun yoksa ya da hücre boşsa `null`. */
  items: (ClassItemInput & { row: number; dnvCode: string | null })[];
  errors: ClassItemRowError[];
}

/**
 * Kalem listesi dosyasının satırları (CSV ya da XLSX'in ilk sayfası; plan Task 25). İlk satır
 * başlıktır; sütun sırası serbesttir. Satır numaralı rapor: hatalı satır uygulanmaz; dosyada
 * tekrar eden kod `DUPLICATE_ITEM_CODE`. Boş satırlar atlanır.
 */
export function parseClassItemRows(rows: readonly (readonly unknown[])[]): ClassItemImport {
  const [header, ...body] = rows;
  if (!header) return { items: [], errors: [{ row: 1, column: null, code: 'EMPTY_FILE' }] };
  const positions = new Map<Column, number>();
  for (const column of CLASS_ITEM_COLUMNS) {
    const index = header.findIndex((cell) => label(cell) === label(column));
    if (index >= 0) positions.set(column, index);
  }
  const dnvAt = header.findIndex((cell) => label(cell) === label(CLASS_ITEM_DNV_COLUMN));
  const missingColumns = CLASS_ITEM_COLUMNS.filter((column) => !positions.has(column));
  if (missingColumns.length > 0) {
    return {
      items: [],
      errors: missingColumns.map((column) => ({ row: 1, column, code: 'MISSING_COLUMN' })),
    };
  }
  const items: ClassItemImport['items'] = [];
  const errors: ClassItemRowError[] = [];
  const seen = new Set<string>();
  body.forEach((cells, index) => {
    const row = index + 2;
    if (cells.every((cell) => String(cell ?? '').trim() === '')) return;
    const cell = (column: Column) => cells[positions.get(column) as number];
    const rowErrors: ClassItemRowError[] = [];
    const intervalCell = String(cell('Interval (months)') ?? '').trim();
    const interval = intervalCell === '' ? null : Number(intervalCell);
    if (interval !== null && !Number.isFinite(interval)) {
      rowErrors.push({ row, column: 'Interval (months)', code: 'INVALID_NUMBER' });
    }
    const dnvCode = dnvAt >= 0 ? String(cells[dnvAt] ?? '').trim() || null : null;
    if (dnvCode !== null && (dnvCode.length > DNV_CODE_MAX || !MAD_CODE_PATTERN.test(dnvCode))) {
      rowErrors.push({ row, column: CLASS_ITEM_DNV_COLUMN, code: 'INVALID_CODE' });
    }
    const candidate = {
      itemCode: String(cell('Item Code') ?? ''),
      description: String(cell('Description') ?? ''),
      category: CATEGORY_ALIASES[label(cell('Category'))] ?? String(cell('Category') ?? ''),
      surveyPrinciple:
        PRINCIPLE_ALIASES[label(cell('Survey Principle'))] ??
        String(cell('Survey Principle') ?? ''),
      intervalMonths: interval !== null && Number.isFinite(interval) ? interval : null,
    };
    const parsed = classItemInputSchema.safeParse(candidate);
    if (!parsed.success) {
      for (const issue of toIssues(parsed.error)) {
        const field = issue.path.split('.')[0] as keyof ClassItemInput;
        rowErrors.push({ row, column: COLUMN_OF[field] ?? null, code: issue.code });
      }
    }
    if (parsed.success && rowErrors.length === 0) {
      const key = classItemKey(parsed.data.itemCode);
      if (seen.has(key)) {
        rowErrors.push({ row, column: 'Item Code', code: 'DUPLICATE_ITEM_CODE' });
      } else {
        seen.add(key);
        items.push({ ...parsed.data, row, dnvCode });
      }
    }
    errors.push(...rowErrors);
  });
  return { items, errors };
}

/* ---------- Sörveyör teyidi [App 1 5.2.9, App 2 5] ---------- */

export const classConfirmationInputSchema = z
  .strictObject({
    surveyor: text(120),
    confirmedOn: localDate,
    items: z
      .array(z.strictObject({ classItemCode: text(32), workRecordId: uuid }))
      .min(1, 'REQUIRED')
      .max(500, 'TOO_LONG'),
    remarks: optionalLines(2000),
  })
  .superRefine((input, ctx) => {
    const seen = new Set<string>();
    input.items.forEach((item, index) => {
      if (seen.has(item.workRecordId)) {
        ctx.addIssue({ code: 'custom', message: 'DUPLICATE_ITEM', path: ['items', index] });
      }
      seen.add(item.workRecordId);
    });
  });
export type ClassConfirmationInput = z.output<typeof classConfirmationInputSchema>;

/**
 * Sörveyör teyidi (gemide girilir, değiştirilemez; K31): sörveyör adı, tarih ve teyit edilen
 * kalemler (başmühendis sörveyinin iş kaydıyla). Teyit tarihi gemi yerel günü; gelecekte olamaz.
 */
export function parseClassConfirmationInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<ClassConfirmationInput> {
  const parsed = classConfirmationInputSchema.safeParse(input);
  if (!parsed.success) return { ok: false, issues: toIssues(parsed.error) };
  const issues: SchemaIssue[] =
    options.today !== undefined && parsed.data.confirmedOn > options.today
      ? [{ path: 'confirmedOn', code: 'FUTURE_DATE' }]
      : [];
  return issues.length > 0 ? { ok: false, issues } : { ok: true, value: parsed.data };
}

export const CLASS_DUE_STATES = [
  'never_surveyed',
  'not_credited',
  'awaiting_confirmation',
  'confirmed',
  'overdue',
] as const;
export type ClassDueState = (typeof CLASS_DUE_STATES)[number];

/**
 * Class Due List satırının durumu: son başmühendis sörveyi ve teyidi. Teyitli sörveyin tarihi
 * teyit tarihi sayılır [App 2 5.1.6]; sonraki vade ondan aralık kadar sonra. PMS'te teyit bir
 * sonraki yıllık denetimdedir [App 2 5.1.1].
 */
export function classDueState(
  item: { intervalMonths: number | null },
  lastCeSurvey: { doneOn: string; ceCredit: boolean } | null,
  confirmation: { confirmedOn: string } | null,
  today: string,
): { state: ClassDueState; dueOn: string | null } {
  if (lastCeSurvey === null) return { state: 'never_surveyed', dueOn: null };
  if (!lastCeSurvey.ceCredit) return { state: 'not_credited', dueOn: null };
  if (confirmation === null) return { state: 'awaiting_confirmation', dueOn: null };
  const dueOn =
    item.intervalMonths === null ? null : addMonths(confirmation.confirmedOn, item.intervalMonths);
  return { state: dueOn !== null && today > dueOn ? 'overdue' : 'confirmed', dueOn };
}

/* ---------- PMS dışı: klas şartı (K25) ---------- */

export interface ClassFindingRef {
  kind: string;
  equipmentId: string | null;
  dueOn: string | null;
  closedOn: string | null;
  deleted: boolean;
}

/**
 * `class_findings` satırlarından `outOfPms` girdisi (M7c; K25): yalnız ekipmana bağlı, silinmemiş
 * klas şartları (CoC); memorandum ve tavsiye PMS dışı yapmaz. Kapanmamış ve vadesi geçmiş CoC'si
 * olan ekipman şart yerine getirilene kadar PMS dışıdır [NR467 Pt A Ch 2 App 1 5.3.3;
 * UR Z20 3.3.3].
 */
export function cocFindingsForOutOfPms(
  findings: readonly ClassFindingRef[],
): { equipmentId: string; dueOn: string | null; closed: boolean }[] {
  return findings.flatMap((f) =>
    f.kind === 'coc' && !f.deleted && f.equipmentId !== null
      ? [{ equipmentId: f.equipmentId, dueOn: f.dueOn, closed: f.closedOn !== null }]
      : [],
  );
}
